import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import {
  BET_LIMIT_AMOUNT_FIELDS, BET_LIMIT_MIX_PARLAY, BET_LIMIT_SPORT_ALL, BetLimitProviderOption, BetLimitSetting, BetLimitSport,
  betLimitCellError, betLimitCellKey, hasBetLimitValue
} from '@core/models/bet-limit-group.model';
import Swal from 'sweetalert2';

type AmountKey = 'min_bet' | 'max_bet' | 'max_amount_per_match';

/**
 * AIO-81599 — step 2: one grid per currency and bet type, a row per sport.
 *
 * A cell exists in `cells` only while it holds a value; an empty input means "use the provider
 * default", whose figure is the grey placeholder. 0 is a value, not empty. "Clear this currency"
 * from the design is not offered: SBO2 figures can only be raised once sent (allow_clear = 0).
 */
@Component({
  selector: 'kt-bet-limit-limits-step',
  templateUrl: './bet-limits-step.component.html',
  styleUrls: ['./bet-limits-step.component.scss'],
})
export class BetLimitsStepComponent implements OnChanges {
  @Input() provider: BetLimitProviderOption;
  @Input() currencies: { id: number; name: string }[] = [];
  @Input() cells: Map<string, BetLimitSetting>;
  @Input() defaultCells: Map<string, BetLimitSetting>;
  @Input() isDefault = false;
  @Input() readonly = false;
  @Input() revision = 0;
  @Output() changed = new EventEmitter<void>();

  currencyId: number = null;
  betTypeCode = '0';

  // kt-dropdown-wo-lazyload writes into a form control; betTypeCode is what the grid reads.
  betTypeForm = new FormGroup({ bet_type_code: new FormControl('0') });
  betTypeItems: { code: string; name: string }[] = [];
  betTypeSelected: { code: string; name: string }[] = [];
  betTypeSettings = {
    singleSelection: true,
    text: 'Select',
    enableFilterSelectAll: false,
    enableSearchFilter: true,
    classes: 'dropdown',
    maxHeight: 300,
    primaryKey: 'code',
    labelKey: 'name',
    noDataLabel: '',
    showCheckbox: false,
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes.provider) {
      this.betTypeItems = (this.provider?.bet_types ?? []).map(bt => ({ code: String(bt.code), name: `${bt.code} — ${bt.label}` }));
      this.selectBetType(this.betTypeItems.some(bt => bt.code === this.betTypeCode) ? this.betTypeCode : '0');
    }
    if ((this.currencyId === null || !this.currencies.some(c => +c.id === this.currencyId)) && this.currencies.length) {
      this.currencyId = +this.currencies[0].id;
    }
  }

  /** Clicking the selected item again clears a single-select list; fall back to ALL. */
  onBetTypeChanged(items: { code: string }[]) {
    this.selectBetType(items?.length ? String(items[0].code) : '0');
  }

  private selectBetType(code: string) {
    this.betTypeCode = code;
    this.betTypeSelected = this.betTypeItems.filter(bt => bt.code === code);
    this.betTypeForm.controls.bet_type_code.setValue(code, { emitEvent: false });
  }

  get fields() {
    return this.provider ? BET_LIMIT_AMOUNT_FIELDS.filter(f => +this.provider[f.flag] === 1) : [];
  }

  /** e.g. "3 — Over/Under", as in the Bet Type dropdown. */
  get betTypeName(): string {
    return this.betTypeItems.find(bt => bt.code === this.betTypeCode)?.name ?? this.betTypeCode;
  }

  get currencyName(): string {
    return this.currencies.find(c => +c.id === this.currencyId)?.name ?? '';
  }

  /** Mix Parlay has one row, All Sports: GSYS refuses it on any other sport. */
  get rows(): BetLimitSport[] {
    const sports = this.provider?.sports ?? [];
    return this.isMixParlay ? sports.filter(s => String(s.code) === BET_LIMIT_SPORT_ALL) : sports;
  }

  get isMixParlay(): boolean {
    return this.betTypeCode === BET_LIMIT_MIX_PARLAY;
  }

  get setCount(): number {
    return this.rows.filter(s => this.isOwn(s)).length;
  }

  /** For the bet type shown, so the dot matches the grid the operator sees. */
  hasValues(currencyId: number): boolean {
    for (const cell of this.cells.values()) {
      if (+cell.settings_currency_id === +currencyId && String(cell.bet_type_code) === this.betTypeCode && hasBetLimitValue(cell)) {
        return true;
      }
    }
    return false;
  }

  cell(sport: BetLimitSport): BetLimitSetting | undefined {
    return this.cells.get(betLimitCellKey(this.currencyId, sport.id, this.betTypeCode));
  }

  isOwn(sport: BetLimitSport): boolean {
    const cell = this.cell(sport);
    return !!cell && hasBetLimitValue(cell);
  }

  value(sport: BetLimitSport, field: AmountKey): string {
    return this.cell(sport)?.[field] ?? '';
  }

  /**
   * The value an empty cell gets, as the api sends it (Rule A): the provider default's same bet
   * type, then its all-bet-types row, then its All Sports row. The default group falls back on
   * its own cells.
   */
  placeholder(sport: BetLimitSport, field: AmountKey): string {
    const source = this.isDefault ? this.cells : this.defaultCells;
    const allSport = (this.provider?.sports ?? []).find(s => String(s.code) === BET_LIMIT_SPORT_ALL);
    const value = [
      betLimitCellKey(this.currencyId, sport.id, this.betTypeCode),
      betLimitCellKey(this.currencyId, sport.id, '0'),
      allSport ? betLimitCellKey(this.currencyId, allSport.id, '0') : null,
    ].map(key => key && source.get(key)?.[field]).find(v => v !== null && v !== undefined && v !== '') ?? null;
    return value === null || value === '' ? '–' : (+value).toLocaleString('en-US', { maximumFractionDigits: 2 });
  }

  error(sport: BetLimitSport): string | null {
    const cell = this.cell(sport);
    return cell && this.provider ? betLimitCellError(cell, this.provider) : null;
  }

  onInput(sport: BetLimitSport, field: AmountKey, raw: string) {
    if (this.readonly) {
      return;
    }
    const value = (raw ?? '').replace(/,/g, '').trim();
    const key = betLimitCellKey(this.currencyId, sport.id, this.betTypeCode);
    const cell = this.cells.get(key) ?? {
      settings_currency_id: this.currencyId,
      bet_limit_provider_sport_id: sport.id,
      bet_type_code: this.betTypeCode,
      min_bet: null,
      max_bet: null,
      max_amount_per_match: null,
    };

    cell[field] = value === '' ? null : value;

    if (hasBetLimitValue(cell)) {
      this.cells.set(key, cell);
    } else {
      this.cells.delete(key);
    }
    this.changed.emit();
  }

  onCopyFromDefault() {
    if (this.readonly) {
      return;
    }
    const sports = this.rows;
    const copies: [BetLimitSport, BetLimitSetting][] = [];

    for (const sport of sports) {
      const values = {} as Record<AmountKey, string | null>;
      for (const f of this.fields) {
        const v = this.placeholder(sport, f.key);
        values[f.key] = v === '–' ? null : v.replace(/,/g, '');
      }
      if (Object.values(values).some(v => v !== null)) {
        copies.push([sport, {
          settings_currency_id: this.currencyId,
          bet_limit_provider_sport_id: sport.id,
          bet_type_code: this.betTypeCode,
          min_bet: values.min_bet ?? null,
          max_bet: values.max_bet ?? null,
          max_amount_per_match: values.max_amount_per_match ?? null,
        }]);
      }
    }

    if (copies.length === 0) {
      Swal.fire('System Message', `The provider default has no values for ${this.currencyName} yet.`, 'info');
      return;
    }

    const apply = () => {
      copies.forEach(([sport, cell]) => this.cells.set(betLimitCellKey(this.currencyId, sport.id, this.betTypeCode), cell));
      this.changed.emit();
    };

    const replaced = copies.filter(([sport]) => this.isOwn(sport)).length;
    if (replaced === 0) {
      apply();
      return;
    }

    // Every sport is set to the default, so say both counts: the empty ones it fills, the set ones it replaces.
    const filled = copies.length - replaced;
    Swal.fire({
      title: 'Copy from Default',
      html: `<p>This sets <b>${copies.length}</b> sport(s) in <b>${this.currencyName}</b>, bet type <b>${this.betTypeName}</b>, to the provider default values:`
        + ` <b>${replaced}</b> with group values are replaced${filled ? `, and <b>${filled}</b> empty are filled` : ''}.</p>`
        + '<p class="m-0">Other currencies and bet types do not change. Nothing is saved until you press Update.</p>',
      icon: 'warning',
      showCancelButton: true,
      reverseButtons: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: 'Replace values',
    }).then(res => res.isConfirmed && apply());
  }

  trackBySport(_: number, sport: BetLimitSport) {
    return sport.id;
  }
}
